import { useState } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import {
  mdiBookOpenVariant,
  mdiCurrencyInr,
  mdiEmailOutline,
  mdiMagnify,
  mdiTrendingDown,
  mdiTrendingUp,
  mdiCloseCircleOutline,
} from "@mdi/js";
import { formatAmountWithCommas } from "../../content_panel/utils/helpers";
import LoadingWidget from "../../../../context_providers/modal/loader";
import PaginationWarehouse from "../inward/PaginationWarehouse";
import {
  StockLedgerProvider,
  useStockLedger,
} from "../contexts/StockLedgerContext";
import { fetchGet } from "../../content_panel/services/serviceClient";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { MobileCard, MobileField } from "../common-components/MobileCard";

function formatInLakh(value: number): string {
  if (value == null || isNaN(value)) return "₹0";
  if (Math.abs(value) >= 100000) {
    return `₹${(value / 100000).toFixed(2)}L`;
  }
  return `₹${formatAmountWithCommas(Math.round(value))}`;
}

export const StockLedgerScreen = () => {
  const {
    rows,
    summary,
    isLoading,
    filters,
    setFilters,
    pagination,
    page,
    setPage,
  } = useStockLedger();

  const [exporting, setExporting] = useState(false);
  const { showToast } = useToast();

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (filters.search) qs.append("search", filters.search);
      if (filters.txn_type && filters.txn_type !== "all")
        qs.append("txn_type", filters.txn_type);
      if (filters.item_type && filters.item_type !== "all")
        qs.append("item_type", filters.item_type);
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/wac/export-stock-ledger-by-email?${qs.toString()}`,
      );
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res.message || "Export will be emailed shortly.",
        });
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to schedule export",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to schedule export",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <main
      id="warehouse-stock-ledger-screen"
      className="wh-mobile-shrink min-h-screen bg-gray-50 p-4 md:p-6"
    >
      <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
        <h1 className="text-2xl font-bold border-l-4 border-blue-600 pl-3 text-gray-800">
          Stock Ledger Dashboard
        </h1>
        <button
          id="warehouse-stock-ledger-export-btn"
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Icon path={mdiEmailOutline} size={0.8} />
          {exporting ? "Scheduling…" : "Export by Email"}
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div
          id="warehouse-stock-ledger-stat-total-entries"
          className="bg-white rounded-lg border shadow-sm p-4 flex justify-between items-center"
        >
          <div>
            <p className="text-sm font-medium text-gray-500">Total Entries</p>
            <p className="text-2xl font-bold text-gray-900">
              {summary.totalEntries}
            </p>
          </div>
          <div className="text-purple-500 rounded-full bg-purple-100 p-2">
            <Icon path={mdiBookOpenVariant} size={1.2} />
          </div>
        </div>
        <div
          id="warehouse-stock-ledger-stat-inward-value"
          className="bg-white rounded-lg border shadow-sm p-4 flex justify-between items-center"
        >
          <div>
            <p className="text-sm font-medium text-gray-500">Inward Value</p>
            <p className="text-2xl font-bold text-green-600">
              {formatInLakh(summary.inwardValue)}
            </p>
          </div>
          <div className="text-green-500 rounded-full bg-green-100 p-2">
            <Icon path={mdiTrendingDown} size={1.2} />
          </div>
        </div>
        <div
          id="warehouse-stock-ledger-stat-outward-value"
          className="bg-white rounded-lg border shadow-sm p-4 flex justify-between items-center"
        >
          <div>
            <p className="text-sm font-medium text-gray-500">Outward Value</p>
            <p className="text-2xl font-bold text-orange-600">
              {formatInLakh(summary.outwardValue)}
            </p>
          </div>
          <div className="text-orange-500 rounded-full bg-orange-100 p-2">
            <Icon path={mdiTrendingUp} size={1.2} />
          </div>
        </div>
        <div
          id="warehouse-stock-ledger-stat-net-movement"
          className="bg-white rounded-lg border shadow-sm p-4 flex justify-between items-center"
        >
          <div>
            <p className="text-sm font-medium text-gray-500">Net Movement</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatInLakh(summary.netMovement)}
            </p>
          </div>
          <div className="text-blue-500">
            <Icon path={mdiCurrencyInr} size={1.5} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-semibold text-gray-700">Filters</div>
        {(filters.search ||
          filters.txn_type !== "all" ||
          filters.item_type !== "all") && (
          <button
            id="warehouse-stock-ledger-clear-filters-btn"
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                search: "",
                txn_type: "all",
                item_type: "all",
              }))
            }
            className="text-sm font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors"
          >
            <Icon path={mdiCloseCircleOutline} size={0.6} />
            Clear All Filters
          </button>
        )}
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Icon
                path={mdiMagnify}
                className="absolute left-3 top-3 translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none"
              />
              <input
                id="warehouse-stock-ledger-search-input"
                type="text"
                value={filters.search}
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    search: e.target.value,
                  }));
                }}
                placeholder="Search item or document..."
                className="w-full h-10 pl-10 pr-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {filters.search && (
              <button
                id="warehouse-stock-ledger-search-clear"
                onClick={() => setFilters((prev) => ({ ...prev, search: "" }))}
                className="text-gray-400 hover:text-red-500 transition-colors"
                title="Clear Search"
              >
                <Icon path={mdiCloseCircleOutline} size={0.7} />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:flex-shrink-0">
            <div className="flex items-center gap-2">
              <select
                id="warehouse-stock-ledger-filter-txn-type"
                value={filters.txn_type}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, txn_type: e.target.value }));
                }}
                className="h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px] flex-1 sm:flex-initial"
              >
                <option value="all">All Transactions</option>
                <option value="IN">IN</option>
                <option value="OUT">OUT</option>
              </select>
              {filters.txn_type !== "all" && (
                <button
                  id="warehouse-stock-ledger-filter-txn-type-clear"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, txn_type: "all" }))
                  }
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Clear Txn Type"
                >
                  <Icon path={mdiCloseCircleOutline} size={0.7} />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <select
                id="warehouse-stock-ledger-filter-item-type"
                value={filters.item_type}
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    item_type: e.target.value,
                  }));
                }}
                className="h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px] flex-1 sm:flex-initial"
              >
                <option value="all">All Item Types</option>
                <option value="FG">FG</option>
                <option value="RM">RM</option>
                {/* <option value="SG">Semi FG</option> */}
              </select>
              {filters.item_type !== "all" && (
                <button
                  id="warehouse-stock-ledger-filter-item-type-clear"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, item_type: "all" }))
                  }
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Clear Item Type"
                >
                  <Icon path={mdiCloseCircleOutline} size={0.7} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Ledger table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <Icon path={mdiBookOpenVariant} className="h-5 w-5 text-gray-600" />
          <h2 className="font-semibold text-gray-800">Transaction Ledger</h2>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
            {pagination.total} found
          </span>
        </div>
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium text-gray-700 whitespace-nowrap">
                  Date
                </th>
                <th className="px-3 py-2.5 text-left font-medium text-gray-700 whitespace-nowrap">
                  Doc Ref
                </th>
                <th className="px-3 py-2.5 text-left font-medium text-gray-700 whitespace-nowrap">
                  Item
                </th>
                <th className="px-3 py-2.5 text-left font-medium text-gray-700 whitespace-nowrap">
                  Type
                </th>
                <th className="px-3 py-2.5 text-left font-medium text-gray-700 whitespace-nowrap">
                  Txn
                </th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-700 whitespace-nowrap">
                  Qty
                </th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-700 whitespace-nowrap">
                  Rate
                </th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-700 whitespace-nowrap">
                  Value
                </th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-700 whitespace-nowrap">
                  Bal Qty
                </th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-700 whitespace-nowrap">
                  Bal Value
                </th>
                <th className="px-3 py-2.5 text-right font-medium text-gray-700 whitespace-nowrap">
                  WAC Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.length > 0 ? (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    id={`warehouse-stock-ledger-row-${row.id}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                      {row.date}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        id={`warehouse-stock-ledger-doc-ref-${row.id}`}
                        type="button"
                        className="font-medium text-left bg-transparent border-none cursor-pointer p-0"
                        onClick={() => {}}
                      >
                        {row.docRef}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {row.itemCode}
                        </p>
                        <p className="text-xs text-gray-500">{row.itemName}</p>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {row.itemType}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {row.txnType === "IN" ? (
                        <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                          <Icon path={mdiTrendingDown} size={0.8} /> IN
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-orange-600 font-medium">
                          <Icon path={mdiTrendingUp} size={0.8} /> OUT
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-gray-900">
                      {formatAmountWithCommas(row.qty)}
                    </td>
                    <td
                      className="px-3 py-2 text-right whitespace-nowrap text-gray-900"
                      title={`Rate: ₹${row.rate}`}
                    >
                      ₹{formatAmountWithCommas(row.rate)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-gray-900">
                      ₹{formatAmountWithCommas(row.value)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-gray-900">
                      {formatAmountWithCommas(row.balQty)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-gray-900">
                      ₹{formatAmountWithCommas(row.balValue)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap text-blue-600 font-medium">
                      ₹{formatAmountWithCommas(row.wacRate!)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr key="no-data">
                  <td colSpan={11} className="text-center py-4 text-gray-500">
                    No transactions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden p-4 space-y-3">
          {rows.length > 0 ? (
            rows.map((row) => (
              <MobileCard
                key={row.id}
                id={`warehouse-stock-ledger-card-${row.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 break-words">
                      {row.itemCode}
                    </p>
                    <p className="text-xs text-gray-500 break-words">
                      {row.itemName}
                    </p>
                  </div>
                  {row.txnType === "IN" ? (
                    <span className="inline-flex items-center gap-1 text-green-600 font-medium text-sm shrink-0">
                      <Icon path={mdiTrendingDown} size={0.7} /> IN
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-orange-600 font-medium text-sm shrink-0">
                      <Icon path={mdiTrendingUp} size={0.7} /> OUT
                    </span>
                  )}
                </div>
                <div className="divide-y divide-gray-100">
                  <MobileField label="Date">{row.date}</MobileField>
                  <MobileField label="Doc Ref">{row.docRef}</MobileField>
                  <MobileField label="Type">
                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {row.itemType}
                    </span>
                  </MobileField>
                  <MobileField label="Qty">
                    {formatAmountWithCommas(row.qty)}
                  </MobileField>
                  <MobileField label="Rate">
                    ₹{formatAmountWithCommas(row.rate)}
                  </MobileField>
                  <MobileField label="Value">
                    ₹{formatAmountWithCommas(row.value)}
                  </MobileField>
                  <MobileField label="Bal Qty">
                    {formatAmountWithCommas(row.balQty)}
                  </MobileField>
                  <MobileField label="Bal Value">
                    ₹{formatAmountWithCommas(row.balValue)}
                  </MobileField>
                  <MobileField label="WAC Rate">
                    <span className="text-blue-600 font-medium">
                      ₹{formatAmountWithCommas(row.wacRate!)}
                    </span>
                  </MobileField>
                </div>
              </MobileCard>
            ))
          ) : (
            <div className="text-center py-4 text-gray-500">
              No transactions found.
            </div>
          )}
        </div>
        {summary.totalEntries === 0 && !isLoading && (
          <div className="py-12 text-center text-gray-500">
            No transactions found.
          </div>
        )}
        {summary.totalEntries > 0 && (
          <PaginationWarehouse
            id="warehouse-stock-ledger-pagination"
            page={page}
            totalPages={pagination.totalPages}
            onPageChange={(newPage: any) => {
              if (newPage >= 1 && newPage <= pagination.totalPages) {
                setPage(newPage);
              }
            }}
          />
        )}
      </div>

      {isLoading && <LoadingWidget />}
    </main>
  );
};

export const StockLedgerDashboard = () => {
  return (
    <StockLedgerProvider>
      <StockLedgerScreen />
    </StockLedgerProvider>
  );
};

export default StockLedgerDashboard;
